import React from 'react';
import { EntityLink } from './shared/EntityLink';

/**
 * EntityLinks.jsx - Reusable link components for navigating between entities
 * Makes the system more intuitive by converting entity references into clickable links
 *
 * NOTE: These are now thin wrappers around the generic EntityLink component
 * for backward compatibility with existing code.
 */

/**
 * CustomerLink - Clickable link to customer detail page
 * @param {number} customerId - Customer ID to link to
 * @param {string} customerName - Display name for the customer (optional)
 * @param {ReactNode} children - Custom content to display (overrides customerName)
 */
export const CustomerLink = ({ customerId, customerName, children, className }) => {
  const displayText = children || customerName;
  return (
    <EntityLink
      type="customer"
      id={customerId}
      displayText={displayText}
      className={className}
    />
  );
};

/**
 * VehicleLink - Clickable link to vehicle detail page
 * @param {number} vehicleId - Vehicle ID to link to
 * @param {string} vehicleInfo - Display info for the vehicle (optional)
 * @param {ReactNode} children - Custom content to display (overrides vehicleInfo)
 */
export const VehicleLink = ({ vehicleId, vehicleInfo, children, className }) => {
  const displayText = children || vehicleInfo;
  return (
    <EntityLink
      type="vehicle"
      id={vehicleId}
      displayText={displayText}
      className={className}
    />
  );
};

/**
 * TransactionLink - Clickable link to transaction detail page
 * @param {number} transactionId - Transaction ID to link to
 * @param {ReactNode} children - Custom content to display (defaults to "Transaction {id}")
 */
export const TransactionLink = ({ transactionId, children, className }) => {
  return (
    <EntityLink
      type="transaction"
      id={transactionId}
      displayText={children}
      className={className}
    />
  );
};

/**
 * PaymentLink - Clickable link to payment detail page
 * @param {number} paymentId - Payment ID to link to
 * @param {ReactNode} children - Custom content to display (defaults to "Payment {id}")
 */
export const PaymentLink = ({ paymentId, children, className }) => {
  return (
    <EntityLink
      type="payment"
      id={paymentId}
      displayText={children}
      className={className}
    />
  );
};

/**
 * RTOLink - Clickable link to RTO agreement detail page
 * @param {number} rtoId - RTO agreement ID to link to
 * @param {ReactNode} children - Custom content to display (defaults to "RTO #{id}")
 */
export const RTOLink = ({ rtoId, children, className }) => {
  return (
    <EntityLink
      type="rto"
      id={rtoId}
      displayText={children}
      className={className}
    />
  );
};
